if (!customElements.get('virtual-try-on')) {
  customElements.define('virtual-try-on', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productId = this.dataset.productId || '';
      this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
      this.openButton = this.querySelector('[data-vto-open]');
      this.shell = this.querySelector('[data-vto-shell]');
      this.drawer = this.querySelector('[data-vto-drawer]');
      this.fileInput = this.querySelector('[data-vto-file]');
      this.generateButton = this.querySelector('[data-vto-generate]');
      this.preview = this.querySelector('[data-vto-preview]');
      this.previewImage = this.querySelector('[data-vto-person-preview]');
      this.status = this.querySelector('[data-vto-status]');
      this.error = this.querySelector('[data-vto-error]');
      this.result = this.querySelector('[data-vto-result]');
      this.resultImage = this.querySelector('[data-vto-result-image]');
      this.download = this.querySelector('[data-vto-download]');
      this.personImage = '';
      this.enabled = false;
      this.previousFocus = null;
      this.touchStartX = null;
      this.bind();
      const check = () => this.checkAvailability();
      window.setTimeout(check, 50);
    }

    bind() {
      this.openButton?.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-vto-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.fileInput?.addEventListener('change', () => this.selectFile());
      this.generateButton?.addEventListener('click', () => this.generate());
      this.querySelector('[data-vto-again]')?.addEventListener('click', () => this.reset(true));
      this.drawer?.addEventListener('touchstart', (event) => { this.touchStartX = event.touches[0]?.clientX ?? null; }, { passive: true });
      this.drawer?.addEventListener('touchend', (event) => {
        const endX = event.changedTouches[0]?.clientX ?? null;
        if (this.touchStartX !== null && endX !== null && endX - this.touchStartX > 80) this.close();
        this.touchStartX = null;
      }, { passive: true });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.shell && !this.shell.hidden) this.close();
      });
    }

    async checkAvailability() {
      if (!this.productId || !this.apiBase) return;
      try {
        const response = await this.request(`/v1/virtual-try-on/status?product_id=${encodeURIComponent(this.productId)}`, { method: 'GET' }, 7000);
        this.tryOnMode = response.data.try_on_mode === 'accessory' ? 'accessory' : 'apparel';
        this.enabled = response.ok && response.data.enabled === true;
        this.hidden = !this.enabled;
      } catch (_) {
        this.enabled = false;
        this.hidden = true;
      }
      window.dispatchEvent(new CustomEvent('iig-vto:status', {
        detail: { enabled: this.enabled, productId: this.productId }
      }));
    }

    open() {
      this.previousFocus = document.activeElement;
      this.shell.hidden = false;
      document.body.classList.add('vto-lock');
      requestAnimationFrame(() => {
        this.shell.classList.add('is-open');
        this.drawer?.focus();
      });
    }

    close() {
      this.shell.classList.remove('is-open');
      document.body.classList.remove('vto-lock');
      window.setTimeout(() => {
        this.shell.hidden = true;
        this.reset(true);
        if (this.previousFocus instanceof HTMLElement) this.previousFocus.focus();
      }, 250);
    }

    async selectFile() {
      this.hideError();
      const file = this.fileInput?.files?.[0];
      if (!file || !['image/jpeg', 'image/png'].includes(file.type) || file.size > 5_000_000) {
        this.reset(false);
        this.showError(this.dataset.fileError || 'Invalid image.');
        return;
      }
      try {
        const data = await this.readAndValidate(file);
        this.personImage = data;
        this.previewImage.src = data;
        this.preview.hidden = false;
        this.generateButton.disabled = false;
        this.result.hidden = true;
      } catch (_) {
        this.reset(false);
        this.showError(this.dataset.fileError || 'Invalid image.');
      }
    }

    readAndValidate(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
          const image = new Image();
          image.onerror = reject;
          image.onload = () => {
            if (image.naturalWidth < 512 || image.naturalHeight < 512) {
              reject(new Error('dimensions'));
              return;
            }
            const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
            const scale = 1800 / longestSide;
            const width = Math.max(1, Math.round(image.naturalWidth * scale));
            const height = Math.max(1, Math.round(image.naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d', { alpha: false });
            if (!context) {
              reject(new Error('canvas'));
              return;
            }
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(image, 0, 0, width, height);
            const normalized = canvas.toDataURL('image/jpeg', 0.92);
            canvas.width = 1;
            canvas.height = 1;
            resolve(normalized);
          };
          image.src = String(reader.result || '');
        };
        reader.readAsDataURL(file);
      });
    }

    async generate() {
      if (!this.personImage || this.generateButton.disabled) return;
      this.hideError();
      this.shell.classList.add('is-busy');
      this.status.hidden = false;
      this.generateButton.disabled = true;
      try {
        const generated = await this.generatePreview(this.personImage);
        this.resultImage.src = generated.imageData;
        this.download.href = generated.imageData;
        this.download.download = generated.downloadName;
        this.result.hidden = false;
        this.preview.hidden = true;
        this.personImage = '';
        this.previewImage.removeAttribute('src');
        if (this.fileInput) this.fileInput.value = '';
      } catch (_) {
        this.showError(this.dataset.error || 'Unable to generate preview.');
        this.generateButton.disabled = false;
      } finally {
        this.status.hidden = true;
        this.shell.classList.remove('is-busy');
      }
    }

    async generatePreview(personImage) {
      if (!this.enabled || !personImage) throw new Error('unavailable');
      const section = this.closest('.shopify-section') || document;
      const variantInput = section.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
      const variantId = String(variantInput?.value || new URL(window.location.href).searchParams.get('variant') || '');
      const token = await this.request('/v1/virtual-try-on/token', {
        method: 'POST',
        body: JSON.stringify({ product_id: this.productId })
      }, 10000);
      if (!token.ok || !token.data.token) throw new Error('token');
      const generated = await this.request('/v1/virtual-try-on/generate', {
        method: 'POST',
        body: JSON.stringify({ product_id: this.productId, token: token.data.token, person_image: personImage, variant_id: variantId })
      }, 150000);
      if (!generated.ok || !generated.data.image_data) throw new Error(['try_on_site_not_visible', 'provider_busy'].includes(generated.data.error) ? generated.data.error : 'generate');
      return {
        imageData: generated.data.image_data,
        downloadName: generated.data.download_name || 'virtual-try-on.png'
      };
    }

    async generateForConcierge(file) {
      if (!this.enabled) throw new Error('unavailable');
      if (!file || !['image/jpeg', 'image/png'].includes(file.type) || file.size > 5_000_000) {
        throw new Error('invalid_image');
      }
      let personImage = '';
      try {
        personImage = await this.readAndValidate(file);
        return await this.generatePreview(personImage);
      } finally {
        personImage = '';
      }
    }

    async request(path, options, timeout) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeout);
      try {
        const headers = { ...(options.headers || {}) };
        if (options.body) headers['Content-Type'] = 'application/json';
        const response = await fetch(this.apiBase + path, {
          ...options,
          credentials: 'omit',
          cache: 'no-store',
          headers,
          signal: controller.signal
        });
        let data = {};
        try { data = await response.json(); } catch (_) {}
        return { ok: response.ok, data };
      } finally {
        window.clearTimeout(timer);
      }
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      this.error.textContent = '';
      this.error.hidden = true;
    }

    reset(clearFile) {
      this.personImage = '';
      this.previewImage.removeAttribute('src');
      this.resultImage.removeAttribute('src');
      this.download.removeAttribute('href');
      this.preview.hidden = true;
      this.result.hidden = true;
      this.status.hidden = true;
      this.generateButton.disabled = true;
      this.shell.classList.remove('is-busy');
      this.hideError();
      if (clearFile && this.fileInput) this.fileInput.value = '';
    }
  });

  window.IIGVirtualTryOnBridge = {
    current() {
      return document.querySelector('virtual-try-on');
    },
    available() {
      return this.current()?.enabled === true;
    },
    generate(file) {
      const element = this.current();
      if (!element || element.enabled !== true) return Promise.reject(new Error('unavailable'));
      return element.generateForConcierge(file);
    }
  };
}
